import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedMemberRouteProps {
  children: React.ReactNode;
}

const ProtectedMemberRoute: React.FC<ProtectedMemberRouteProps> = ({ children }) => {
  const { isLoggedIn, loading } = useMemberAuth();
  const navigate = useNavigate();
  const [checkComplete, setCheckComplete] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  
  // Check for valid session (context, localStorage, OR Supabase session)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const adminSession = localStorage.getItem('adminSession') === 'true';

      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      let hasLocalUser = false;
      if (storedUser && storedToken) {
        try {
          const userData = JSON.parse(storedUser);
          hasLocalUser = !!(userData.id && userData.memberType);
        } catch {
          hasLocalUser = false;
        }
      }

      if (cancelled) return;
      setHasValidSession(adminSession || hasLocalUser || isLoggedIn);
      setCheckComplete(true);
    };
    run();
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  useEffect(() => {
    // Only redirect after checks are complete and context is not loading
    if (checkComplete && !loading && !hasValidSession) {
      navigate('/auth', { replace: true });
    }
  }, [checkComplete, loading, hasValidSession, navigate]);

  // Show loading while context is loading OR we haven't checked localStorage yet
  if (loading || !checkComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow valid sessions through
  if (hasValidSession) {
    return <>{children}</>;
  }

  return null;
};

export default ProtectedMemberRoute;
